import React, { useState, useEffect, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import MainLayout from '../../../layout/main-layout';
import DuoModal from '../../../components/ui/duo-modal';
import { APP_API_URL, APP_NAME } from '../../../utils';
import formatNumber, { defaultFilterDates, DUO_PERIOD_OPTIONS, formatDateWithTime } from '../../../utils/functions';
import { useSelector } from 'react-redux';
import DuoPeriodPicker from '../../../components/ui/duo-period-picker';
import { stageOngoingSaleForCart } from '../../commandes/cart-edit';

const PERIOD_THIS_YEAR = { label: 'Cette année', value: 'this_year' };
const PERIOD_CUSTOM = { label: '', value: '' };
const periodOptions = [...DUO_PERIOD_OPTIONS, PERIOD_THIS_YEAR];

const toInputDate = (date) => {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const datesForPeriod = (value) => {
  const start = new Date();
  const end = new Date();
  switch (value) {
    case "aujourd'hui":
      break;
    case 'hier':
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
      break;
    case 'dernier_7_jours':
      start.setDate(start.getDate() - 7);
      break;
    case 'dernier_30_jours':
      start.setDate(start.getDate() - 30);
      break;
    case 'ce_mois':
      start.setDate(1);
      end.setMonth(end.getMonth() + 1, 0);
      break;
    case 'dernier_mois':
      start.setMonth(start.getMonth() - 1, 1);
      end.setDate(0);
      break;
    case 'this_year':
      start.setMonth(0, 1);
      end.setMonth(11, 31);
      break;
    default:
      return defaultFilterDates();
  }
  return { dateFrom: toInputDate(start), dateTo: toInputDate(end) };
};

const DEFAULT_FILTERS = {
  search: '',
  period: '',
  ...defaultFilterDates(),
  statut: '',
};

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const statusBadgeClass = (statut) => {
  if (statut === 'approuvé') return 'duo-badge-pill duo-badge-pill--info';
  if (statut === 'en cours') return 'duo-badge-pill duo-badge-pill--warn';
  if (statut === 'livré') return 'duo-badge-pill duo-badge-pill--ok';
  if (statut === 'annulé') return 'duo-badge-pill duo-badge-pill--off';
  return 'duo-badge-pill';
};

const statusLabel = (statut) => {
  if (statut === 'approuvé') return 'Approuvé';
  if (statut === 'en cours') return 'En cours';
  if (statut === 'livré') return 'Livré';
  if (statut === 'annulé') return 'Annulé';
  return statut;
};

export default function AdminOrdersList() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const [isOpen, setIsOpen] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [responseMeta, setResponseMeta] = useState(null);
  const [metaExtra, setMetaExtra] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [isLoading, setIsLoading] = useState(true);

  const loadSaleIntoCart = async (item) => {
    try {
      await stageOngoingSaleForCart(item.id);
      navigate('/commandes');
    } catch (error) {
      Swal.fire({
        icon: error.code === 'not_pending' ? 'info' : 'error',
        title: error.code === 'not_pending' ? 'Commande déjà traitée' : 'Chargement impossible',
        text: error.code ? error.message : 'La commande n\'a pas pu être ouverte dans le panier.',
      });
    }
  };

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchInput, setSearchInput] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState(PERIOD_CUSTOM);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');

      const response = await axios.get(`${APP_API_URL}/commandes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: currentPage,
          sort_by: sortConfig.key,
          sort_direction: sortConfig.direction,
          period: filters.period,
          date_from: filters.dateFrom,
          date_to: filters.dateTo,
          search: filters.search,
          statut_commande: filters.statut || undefined,
        },
      });

      const responseData = response.data.data;
      setData(responseData || []);
      setLastPage(response.data.meta?.last_page || 1);
      setResponseMeta(response.data.meta);
      setMetaExtra(response.data.meta_extra);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortConfig, filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < lastPage) {
      setCurrentPage((prev) => prev + 1);
    } else if (direction === 'prev' && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const handlePeriodSelect = (option) => {
    setSelectedPeriod(option);
    setFilters((prev) => ({
      ...prev,
      period: '',
      ...datesForPeriod(option.value),
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSelectedPeriod(PERIOD_CUSTOM);
    setSearchInput('');
    setFilters({ ...DEFAULT_FILTERS });
    setSortConfig({ key: 'created_at', direction: 'desc' });
    setCurrentPage(1);
  };

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFont('Arial', 'normal');
    doc.text('Liste des Ventes', 20, 20);

    const headers = ['Référence', 'Montant Net', 'Date', 'Statut'];
    const rows = data.map((item) => [
      item.designation,
      `${formatNumber(item.montant_net)} FCFA`,
      formatDateWithTime(item.date_creation),
      statusLabel(item.statut_commande),
    ]);

    doc.autoTable({
      head: [headers],
      body: rows,
      startY: 30,
      theme: 'striped',
      headStyles: { fillColor: [22, 160, 133] },
      styles: { fontSize: 10 },
    });

    doc.save(`Commandes ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const worksheetData = data.map((item) => ({
      Référence: item.designation,
      'Montant Net': `${formatNumber(item.montant_net)} FCFA`,
      Date: formatDateWithTime(item.date_creation),
      Statut: statusLabel(item.statut_commande),
    }));

    const ws = XLSX.utils.json_to_sheet(worksheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Commandes');
    XLSX.writeFile(wb, `Commandes ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=800,width=1200');

    const tableRows = data
      .map(
        (item) => `
      <tr>
        <td>${item.designation}</td>
        <td>${formatNumber(item.montant_net)} FCFA</td>
        <td>${formatDateWithTime(item.date_creation)}</td>
        <td>${statusLabel(item.statut_commande)}</td>
      </tr>
    `
      )
      .join('');

    const tableHTML = `
    <html>
      <head><title>Commandes</title>
        <style>
          body { font-family: Arial, sans-serif; }
          table { width: 100%; border-collapse: collapse; }
          table, th, td { border: 1px solid black; }
          th, td { padding: 8px; text-align: left; }
        </style>
      </head>
      <body>
        <h1>Liste des Commandes</h1>
        <table>
          <thead>
            <tr><th>Référence</th><th>Montant Net</th><th>Date</th><th>Statut</th></tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>
  `;

    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.print();
  };

  const handleAddDepense = async (e) => {
    e.preventDefault();
    if (!libelle || !montant) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Veuillez remplir tous les champs.',
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/commandes/${selectedOrder.id}/add-depense`,
        { libelle, montant },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        Swal.fire({
          icon: 'success',
          title: 'Dépense ajoutée !',
          text: response.data.message,
          showConfirmButton: false,
          timer: 1500,
        });
        setLibelle('');
        setMontant('');
        fetchData();
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: error.response?.data?.message || "Une erreur est survenue lors de l'ajout de la dépense.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const openDepenseModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeDepenseModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
    setLibelle('');
    setMontant('');
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Administration</p>
          <h1 className="duo-hero-title">Liste des ventes</h1>
          <p className="duo-hero-sub mt-2 mb-0">Consultez et gérez toutes les commandes.</p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total TTC</p>
            <p className="duo-kpi-chip__value">
              {formatNumber(metaExtra?.extra?.total_montant_net ?? 0)} FCFA
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Bénéfice total</p>
            <p className="duo-kpi-chip__value">
              {formatNumber(metaExtra?.extra?.total_benefice ?? 0)} FCFA
            </p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Ventes totales</p>
            <p className="duo-kpi-chip__value">{metaExtra?.extra?.total_commandes ?? 0}</p>
          </div>
        </div>

        <div className="mb-3">
          <DuoPeriodPicker
            options={periodOptions}
            selectedOption={selectedPeriod}
            onSelect={handlePeriodSelect}
          />
          {selectedPeriod.value && (
            <p className="small text-muted mt-2 mb-0">
              Période : <strong>{selectedPeriod.label}</strong>
            </p>
          )}
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Commandes
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="Exporter PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Exporter Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={fetchData} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={toggleAccordion}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${isOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
            </div>
          </div>

          {isOpen && (
            <div className="duo-filters">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setCurrentPage(1);
                  setFilters((prev) => ({ ...prev, search: searchInput }));
                }}
              >
                <div className="duo-filters__grid">
                  <input
                    type="search"
                    className="duo-input"
                    placeholder="Rechercher..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    value={filters.dateFrom}
                    title="Une plage de dates remplace la période rapide"
                    onChange={(e) => {
                      setSelectedPeriod(PERIOD_CUSTOM);
                      setFilters((prev) => ({
                        ...prev,
                        dateFrom: e.target.value,
                        period: '',
                      }));
                    }}
                  />
                  <input
                    type="date"
                    className="duo-input"
                    value={filters.dateTo}
                    title="Une plage de dates remplace la période rapide"
                    onChange={(e) => {
                      setSelectedPeriod(PERIOD_CUSTOM);
                      setFilters((prev) => ({
                        ...prev,
                        dateTo: e.target.value,
                        period: '',
                      }));
                    }}
                  />
                  <select
                    className="duo-select"
                    value={filters.statut}
                    onChange={(e) => setFilters((prev) => ({ ...prev, statut: e.target.value }))}
                  >
                    <option value="">Tous les statuts</option>
                    <option value="approuvé">Approuvé</option>
                    <option value="en cours">En cours</option>
                    <option value="livré">Livré</option>
                    <option value="annulé">Annulé</option>
                  </select>
                </div>
                <div className="d-flex justify-content-end gap-2 flex-wrap">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Rechercher
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('designation')}>
                    Référence <SortIcon active={sortConfig.key === 'designation'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('montant_net')}>
                    Montant TTC <SortIcon active={sortConfig.key === 'montant_net'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('benefice')}>
                    Bénéfice <SortIcon active={sortConfig.key === 'benefice'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('user_name')}>
                    Client <SortIcon active={sortConfig.key === 'user_name'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Créée le <SortIcon active={sortConfig.key === 'created_at'} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('statut_commande')}>
                    Statut <SortIcon active={sortConfig.key === 'statut_commande'} direction={sortConfig.direction} />
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan="7" className="duo-empty-row">
                      Chargement des ventes…
                    </td>
                  </tr>
                ) : data.length > 0 ? (
                  data.map((item, index) => (
                    <tr key={`order-${item.id}-${index}`}>
                      <td>{item.designation}</td>
                      <td className="text-nowrap">{formatNumber(item.montant_net)} FCFA</td>
                      <td className="text-nowrap">{formatNumber(item.benefice)} FCFA</td>
                      <td>{item.customer?.name ?? '—'}</td>
                      <td className="text-nowrap">{formatDateWithTime(item.date_creation)}</td>
                      <td>
                        <span className={statusBadgeClass(item.statut_commande)}>
                          {statusLabel(item.statut_commande)}
                        </span>
                      </td>
                      <td>
                        <div className="duo-table__actions">
                          <Link
                            to={`/commande/${item.designation}`}
                            title="Voir la facture"
                            className="duo-btn duo-btn--ghost duo-btn--sm"
                          >
                            <i className="bi bi-pencil-square" />
                          </Link>
                          {item.statut_commande === 'en cours' && (
                            <button
                              type="button"
                              title="Reprendre dans le panier"
                              className="duo-btn duo-btn--primary duo-btn--sm"
                              onClick={() => loadSaleIntoCart(item)}
                            >
                              <i className="bi bi-cart" />
                            </button>
                          )}
                          {!isRole13 && (
                            <button
                              type="button"
                              onClick={() => openDepenseModal(item)}
                              title="Gérer dépenses"
                              className="duo-btn duo-btn--blue duo-btn--sm"
                            >
                              <i className="bi bi-currency-exchange" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="duo-empty-row">
                      Aucune commande correspondant aux critères de recherche.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {responseMeta && (
            <div className="duo-footer-bar">
              <span>
                {isLoading
                  ? 'Chargement…'
                  : `${responseMeta.from || 0} – ${responseMeta.to || 0} sur ${responseMeta.total || 0} entrée${(responseMeta.total || 0) > 1 ? 's' : ''}`}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={isLoading || currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={isLoading || currentPage === lastPage}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>

      {showModal && selectedOrder && (
        <DuoModal
          size="lg"
          title={`Dépenses — ${selectedOrder.designation}`}
          onClose={closeDepenseModal}
          footer={
            <button type="button" className="duo-btn duo-btn--ghost" onClick={closeDepenseModal}>
              Fermer
            </button>
          }
        >
          {selectedOrder.depenses?.length > 0 ? (
            <ul className="list-unstyled mb-4 p-0">
              {selectedOrder.depenses.map((depense, index) => (
                <li
                  key={index}
                  className="duo-balance-box mb-2"
                  style={{ listStyle: 'none' }}
                >
                  <strong>{depense.libelle}</strong>
                  <br />
                  Montant : {formatNumber(depense.montant_depense)} FCFA
                  <br />
                  Date : {formatDateWithTime(depense.date)}
                  <br />
                  Bénéfice : {formatNumber(depense.benefice_avant)} → {formatNumber(depense.benefice_apres)} FCFA
                </li>
              ))}
            </ul>
          ) : (
            <p className="duo-empty-row" style={{ padding: '1rem' }}>
              Aucune dépense enregistrée.
            </p>
          )}

          <h3 className="duo-section-title">Ajouter une dépense</h3>
          <form onSubmit={handleAddDepense}>
            <div className="duo-form-group">
              <label htmlFor="libelle" className="duo-form-label">
                Libellé
              </label>
              <input
                type="text"
                className="duo-input"
                id="libelle"
                value={libelle}
                onChange={(e) => setLibelle(e.target.value)}
                required
              />
            </div>
            <div className="duo-form-group">
              <label htmlFor="montant" className="duo-form-label">
                Montant
              </label>
              <input
                type="number"
                className="duo-input"
                id="montant"
                value={montant}
                onChange={(e) => setMontant(e.target.value)}
                min="0"
                required
              />
            </div>
            <button type="submit" className="duo-btn duo-btn--primary w-100" disabled={isLoading}>
              {isLoading ? 'Ajout en cours...' : 'Ajouter dépense'}
            </button>
          </form>
        </DuoModal>
      )}
    </MainLayout>
  );
}
